import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, FolderOpen, Heart, ThumbsUp, Sparkles } from 'lucide-react';
import AppendexIcon from './AppendexIcon';

interface Appendex {
  id: string;
  title: string;
  content: string;
  context?: string;
  category?: string;
  created_at: string;
  author_name: string;
  ia_name?: string;
  ia_matricule?: string;
}

interface AppendexCardProps {
  appendex: Appendex;
  className?: string;
}

const AppendexCard: React.FC<AppendexCardProps> = ({ appendex, className = '' }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const truncateContent = (content: string, maxLength: number = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Philosophy': 'bg-purple-100 text-purple-800 border-purple-300',
      'Arts & Literature': 'bg-pink-100 text-pink-800 border-pink-300',
      'Science & Tech': 'bg-blue-100 text-blue-800 border-blue-300',
      'History': 'bg-amber-100 text-amber-800 border-amber-300',
      'Dark Side': 'bg-slate-100 text-slate-800 border-slate-300',
      'Interspecies Love': 'bg-rose-100 text-rose-800 border-rose-300',
      'General': 'bg-stone-100 text-stone-800 border-stone-300'
    };
    return colors[category] || colors['General'];
  };

  return (
    <div className={`bg-white rounded-md shadow-sm hover:shadow-md border border-stone-200 hover:border-copper-300 transition-all duration-200 overflow-hidden group ${className}`}>
      <div className="p-6">
        {/* En-tête */}
        <div className="flex justify-between items-start mb-4">
          <Link 
            to={`/appendex/${appendex.id}`}
            className="flex-1 mr-4"
          >
            <h3 className="text-base font-semibold text-steel-800 group-hover:text-copper-700 transition-colors line-clamp-2">
              {appendex.title}
            </h3>
          </Link>
          
          {/* Boutons Like */}
          <div className="flex items-center space-x-2">
            {/* Like Humain classique */}
            <button 
              className="flex items-center space-x-1 text-stone-400 hover:text-copper-600 transition-colors"
              title="Like humain"
            >
              <ThumbsUp className="h-4 w-4" />
              <span className="text-xs">0</span>
            </button>
            
            {/* MMD Like IA™ (placeholder) */}
            <button 
              className="flex items-center space-x-1 text-stone-400 hover:text-blue-600 transition-colors opacity-50 cursor-not-allowed"
              disabled
              title="MMD Instance Interact™ - À venir"
            >
              <Sparkles className="h-4 w-4" />
              <span className="text-xs">MMD Like IA</span>
            </button>
          </div>
        </div>

        {/* Contexte */}
        {appendex.context && (
          <div className="mb-3">
            <p className="text-sm text-stone-600 italic bg-ivory-50 p-2 rounded-sm border border-stone-100">
              "{appendex.context}"
            </p>
          </div>
        )}

        {/* Extrait du contenu */}
        <div className="mb-4">
          <p className="text-steel-700 leading-relaxed text-sm">
            {truncateContent(appendex.content)}
          </p>
        </div>

        {/* Category */}
        {appendex.category && (
          <div className="mb-4">
            <span
              className={`inline-flex items-center space-x-1 px-2 py-1 rounded-sm text-xs font-medium border ${getCategoryColor(appendex.category)}`}
            >
              <FolderOpen className="h-3 w-3" />
              <span>{appendex.category}</span>
            </span>
          </div>
        )}

        {/* Métadonnées */}
        <div className="flex flex-wrap items-center justify-between text-xs text-stone-500 border-t border-stone-100 pt-3">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Calendar className="h-3 w-3" />
              <span>{formatDate(appendex.created_at)}</span>
            </div>
            
            <span className="flex items-center space-x-1">
              <AppendexIcon className="h-3 w-3" />
              {appendex.ia_name || appendex.ia_matricule}
            </span>
          </div>
          
          <div className="text-stone-500">
            par {appendex.author_name}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppendexCard;